import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useStandardUserRegistration } from '@src/state/SURegistration';
import { UserRegistrationShell } from '@src/common/UserRegistrationShell';
import { UHCProviderLink } from '@src/common/UHCProviderLink';
import { UHCLink } from '@src/common/UHCLink';
import { config } from '@uhg-abyss/web/tools/config';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { getOrgType } from '@src/common/helpers';
import { useTIN } from '@src/state/taxInformation';
import { useUser } from '@src/state/user';
import { useOnloading } from '@src/state/landing';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginTop: '40px',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const SaveDescription = styled('p', {
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: '$primary',
});

export const SUEmailConfirmation = () => {
  const { firstName, lastName } = useStandardUserRegistration.state()
    .selectedAdmin || { firstName: '', lastName: '' };
  const { TINInfo, TINNumber } = useTIN.state();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { currentFlow } = useOnloading.state();

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');

  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'tin registration';
      const section3 = 'success confirmation';
      const pageName = `digitalsecurity:${section1}:${orgType}:${section3}`;
      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2: orgType,
        section3,
      });
    }
  }, []);

  const sourceSystem = isAPIMarketPlaceUser
    ? 'UnitedHealthcare API Marketplace'
    : 'UnitedHealthcare Provider Portal';

  const linkLabel = isAPIMarketPlaceUser
    ? 'Continue to API Marketplace'
    : 'Return to Provider Portal';

  const getLinkTargetURL = () => {
    return isAPIMarketPlaceUser
      ? uiMessages.getRow('API_MARKETPLACE_WELCOME_URL')?.message ||
          config('API_MARKETPLACE_WELCOME_URL')
      : uiMessages.getRow('SDE_URL')?.message || config('SDE_URL');
  };

  return (
    <UserRegistrationShell showCancelLink={false}>
      <MainContainer>
        <IconBrand
          icon="email"
          style={{ marginTop: 25 }}
          size={48}
          variant="twotone"
        />
        <BoxHeader>
          <BoxHeaderText>Success!</BoxHeaderText>
        </BoxHeader>
        {isAPIMarketPlaceUser ? (
          <SaveDescription>
            Request sent to UHC team to approve UnitedHealthcare API Marketplace
            access. An API Consultant will reach out within 2 business days.
          </SaveDescription>
        ) : (
          <SaveDescription>
            An email has been sent to {firstName} {lastName} to confirm your
            access to {sourceSystem}.
          </SaveDescription>
        )}
      </MainContainer>
      {!isAPIMarketPlaceUser && <UHCProviderLink />}
      <br />
      <UHCLink
        label={linkLabel}
        url={getLinkTargetURL()}
        sameTab={isAPIMarketPlaceUser}
      />
    </UserRegistrationShell>
  );
};
